import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Box,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  Typography,
  useTheme,
  Modal,
} from "@mui/material";
import useReward from "../../hooks/useReward";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
import "./RewardTab.css";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";
import { calculateLevelAndExperience, calculateDayOfUse } from "../../utils/math.utils";

const RewardTab = () => {
  const { t } = useTranslation();
  const { reward, isLoading } = useReward();
  const { isProticoAuthenticated } = useWeb3Auth();
  const { roomDomain } = useWhiteboardRoom();
  const [modalOpen, setModalOpen] = useState(false);
  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };
  const theme = useTheme();
  if (isLoading) {
    return (<div style={{margin:"auto"}}>
      <CircularProgress
      style={{ color: theme.brandColor }}
      size={20}
    />
    </div>
      
    );
  }

  return isProticoAuthenticated ? (
    <div className="reward-tab-container">
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="redeem-list-title"
        aria-describedby="redeem-list-description"
        disableAutoFocus
      >
        <Box className="redeem-list-modal">
          <h2>{t("Wallet_Popper_Point_Redeem_Modal_Title")}</h2>
          <ul>
            
            <li>
              <div className="redeem-list-item">
                <div><a href="https://thepossible.io/" target="_blank" rel="noopener noreferrer">Possible</a></div>
                <div><a href="https://thepossible.io/" target="_blank" rel="noopener noreferrer"><span className="redeem-button">{t("Wallet_Popper_Point_Redeem_Modal_List_Check")}</span></a></div>
              </div>
            </li>
            <li>
              <div className="redeem-list-item">
                <div><a href="https://botify.ai/" target="_blank" rel="noopener noreferrer">Botify AI</a></div>
                <div><a href="https://botify.ai/" target="_blank" rel="noopener noreferrer"><span className="redeem-button">{t("Wallet_Popper_Point_Redeem_Modal_List_Check")}</span></a></div>
              </div>
            </li>
            <p className="redeem-list-more">{t("Wallet_Popper_Point_Redeem_Modal_List_More")}</p>
          </ul>
        </Box>
      </Modal>
      <p className="reward-tab-title">
        <span className="reward-subtitle-emoji">
        🏅
        </span>
        {reward.is_spam_user
          ? t("Wallet_Popper_PointTitle") + " -P (Lv.-)"
          : t("Wallet_Popper_PointTitle") +
            " " +
            reward.total +
            " P"}
      </p>
      <div><span className="redeem-list-viewer" onClick={handleOpenModal}>{t("Wallet_Popper_Point_Redeem")}</span></div>
      <Box sx={{ display: "flex", alignItems: "center", margin: "15px 0 20px 0" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress
            variant="determinate"
            value={
              (calculateLevelAndExperience(reward.experience)
                .remainingExperience /
                calculateLevelAndExperience(reward.experience)
                  .experienceNeeded) *
              100
            }
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "12px" }}
          >
            {reward.is_spam_user
              ? "-/-"
              : `Lv. ${calculateLevelAndExperience(reward.experience).level} ${
                  calculateLevelAndExperience(reward.experience)
                    .remainingExperience
                }/${
                  calculateLevelAndExperience(reward.experience)
                    .experienceNeeded
                }`}
          </Typography>
        </Box>
      </Box>
      <hr
        style={{
          border: "5px dotted #333",
          borderStyle: "none none dotted", 
        }}
      ></hr>
      <div className="reward-list-container">
        {reward.is_spam_user ? (
          <>{t("Spam_User_Alert")}</>
        ) : (
          <>
            <p className="reward-tab-title">
              <span className="reward-subtitle-emoji">
                📈
              </span>
              {t("Quest_Multiplier")}: {reward.multiplier}x
            </p>
            <div className="reward-tab-multiplier-reminder">
              <p>
                {t("Quest_Multiplier_Reminder_1") + " " + calculateDayOfUse(reward.days_of_use).remainingDays + " " + t("Quest_Multiplier_Reminder_2") + " " + calculateDayOfUse(reward.days_of_use).nextMultiplier + "x"}
              </p>
            </div>
            <hr
              style={{
                border: "5px dotted #333",
                borderStyle: "none none dotted", 
              }}
            ></hr>
            <List>
              <p className="reward-tab-title">
                <span className="reward-subtitle-emoji">
                  📆 
                </span>
                {t("Daily_Quest")} ({reward.today_points}P/
                {reward.today_max_points}P)</p>
              <ListItem className="reward-tab-item">
                {reward.daily_login ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Login_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_message ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Message_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_reaction ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Reaction_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_response ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Reply_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {!!reward.daily_ad_click ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Ads_Click_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_poll ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Poll_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_trending_room ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Trending_Lobby_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_join_small_apps ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Join_Small_Apps_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_win_small_apps ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Win_Small_Apps_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_lobby_msg_1 ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("First_Lobby_Messages_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_lobby_msg_2 ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Second_Lobby_Messages_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.daily_lobby_msg_3 ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Third_Lobby_Messages_Quest")}
                </span>
              </ListItem>
            </List>
            <hr
              style={{
                border: "5px dotted #333",
                borderStyle: "none none dotted", 
              }}
            ></hr>
            <p className="reward-tab-title" style={{ marginTop: "25px" }}>
              <span className="reward-subtitle-emoji">
                🎯
              </span>
              {t("One_Time_Quest")}
            </p>
            <List>
              <ListItem className="reward-tab-item">
                {reward.avatar_changed ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Change_Avatar_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.name_changed ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Change_Name_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.description_changed ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Change_Description_Quest")}
                </span>
              </ListItem>
              <ListItem className="reward-tab-item">
                {reward.ens_name_changed ? (
                  <CheckCircleIcon sx={{ fontSize: "15px" }} />
                ) : (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "15px", color: "#888" }}
                  />
                )}
                <span className="reward-tab-unit-description">
                  {t("Change_Ens_Quest")}
                </span>
              </ListItem>
            </List>
          </>
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};

export default RewardTab;
